// Заметки (README §6). Меню: «проекты» → заметки (дерево папок из API),
// «журнал» (свежие правки базы), «+ заметка»; граф и поиск — свёрнутым «Видом».
// Рабочая область — ленивый чанк NotesView (редактор TipTap).

import { lazy, useEffect, useState } from 'react'
import { notesApi } from '../../api'
import type { FeedItem, TreeNode } from '../../protocol'
import { countNotes, useAppStore } from '../../store/appStore'
import { SideMenu } from '../../ui/SideMenu'
import type { SideItem } from '../../ui/SideMenu'
import type { SectionModule } from '../types'

const View = lazy(() => import('./NotesView'))

const JOURNAL_LIMIT = 12

function noteName(node: TreeNode): string {
  return node.name.replace(/\.md$/i, '')
}

/** Содержимое папки одним списком: меню — три уровня, глубже не бывает. */
function flatten(nodes: TreeNode[], prefix = ''): TreeNode[] {
  return nodes.flatMap((node) =>
    node.kind === 'note'
      ? [{ ...node, name: prefix + noteName(node) }]
      : flatten(node.children, `${prefix}${node.name}/`),
  )
}

function when(ts: number): string {
  const d = new Date(ts * 1000)
  const now = new Date()
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
  }
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }).replace('.', '')
}

/** Журнал — последние правки базы (тот же источник, что у Ленты). */
function useJournal(): FeedItem[] {
  const tree = useAppStore((s) => s.tree)
  const [items, setItems] = useState<FeedItem[]>([])
  useEffect(() => {
    let dead = false
    notesApi.feed(JOURNAL_LIMIT)
      .then((page) => { if (!dead) setItems(page.items) })
      .catch(() => undefined)
    return () => { dead = true }
  }, [tree])
  return items
}

function Sidebar() {
  const tree = useAppStore((s) => s.tree)
  const treeError = useAppStore((s) => s.treeError)
  const notePath = useAppStore((s) => s.notePath)
  const section = useAppStore((s) => s.section)
  const baseView = useAppStore((s) => s.baseView)
  const openNote = useAppStore((s) => s.openNote)
  const setBaseView = useAppStore((s) => s.setBaseView)
  const createNote = useAppStore((s) => s.createNote)
  const journal = useJournal()
  const on = section === 'notes'
  const isDoc = on && baseView !== 'graph' && baseView !== 'search'

  const leaf = (node: TreeNode): SideItem => ({
    id: node.path,
    label: node.kind === 'note' ? noteName(node) : node.name,
    title: node.path,
    active: isDoc && node.path === notePath,
    onSelect: () => openNote(node.path),
  })

  const items: SideItem[] = tree.map((node) =>
    node.kind === 'folder'
      ? {
          id: node.path,
          label: node.name,
          meta: String(countNotes(node.children)),
          defaultFolded: !(notePath ?? '').startsWith(`${node.path}/`),
          children: flatten(node.children).map(leaf),
        }
      : leaf(node),
  )

  const add = () => {
    const title = `заметка ${new Date().toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`
    const folder = notePath && notePath.includes('/') ? notePath.split('/').slice(0, -1).join('/') : ''
    void createNote(folder, title)
  }

  return (
    <SideMenu
      screen="notes"
      sections={[
        {
          id: 'projects',
          title: 'Проекты',
          count: String(countNotes(tree)),
          onAdd: add,
          addTitle: 'новая заметка',
          items: [
            ...items,
            ...(treeError ? [{ id: 'err', label: 'база недоступна', title: treeError, action: true }] : []),
            {
              id: 'journal',
              label: 'журнал',
              defaultFolded: true,
              children: journal.length
                ? journal.map((it) => ({
                    id: `j:${it.path}`,
                    label: it.title,
                    title: `${it.path} — ${it.change === 'created' ? 'создана' : 'изменена'}${it.source === 'agent' ? ' агентом' : ''}`,
                    meta: when(it.at),
                    metaTone: it.read ? 'dim' : 'link',
                    onSelect: () => openNote(it.path),
                  } satisfies SideItem))
                : [{ id: 'j:none', label: 'пока пусто', action: true }],
            },
            { id: 'add', label: '+ заметка', action: true, onSelect: add },
            // раздел «Вид» убран (Степан, 23.09.2026); если граф/поиск остались открытыми — выход к документу
            ...(on && baseView !== 'document' ? [{ id: 'v-doc', label: '← к документу', action: true, onSelect: () => setBaseView('document') }] : []),
          ],
        },
      ]}
    />
  )
}

function useRail() {
  const n = useAppStore((s) => countNotes(s.tree))
  return { count: String(n), tip: `заметки: ${n}` }
}

// keepAlive: документ держит таймер автосохранения — уход в другой раздел
// не должен терять правки.
const notes: SectionModule = { Sidebar, View, keepAlive: true, useRail }
export default notes
